/**
 * A ram-air canopy, for the last few hundred metres of a drop.
 *
 * One merged, vertex-coloured mesh: nine cells laid along an arc, each a
 * slightly thicker box toward the centre, in alternating stripes, with the
 * suspension lines running down to the harness. The canopy's origin is the
 * harness point, so it can be dropped straight onto the character's shoulders.
 */

import type { BufferGeometry } from 'three';
import {
  BoxGeometry,
  BufferAttribute,
  Color,
  CylinderGeometry,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const CELLS = 9;
const SPAN = 8.4;
const CHORD = 2.9;
/** Harness to the canopy's underside. */
const LINE_LENGTH = 5.6;
/** Radius of the canopy's spanwise arc. */
const ARC = 7.5;
const STRIPES = [0xe2473a, 0xf6f0e1, 0xf3b63c];

function painted(geometry: BufferGeometry, hex: number): BufferGeometry {
  const flat = geometry.toNonIndexed();
  geometry.dispose();
  flat.deleteAttribute('uv');
  flat.computeVertexNormals();
  const c = new Color(hex);
  const count = flat.getAttribute('position').count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) colors.set([c.r, c.g, c.b], i * 3);
  flat.setAttribute('color', new BufferAttribute(colors, 3));
  return flat;
}

export function createParachute(): Mesh {
  const parts: BufferGeometry[] = [];
  const angleStep = SPAN / ARC / CELLS;
  const anchors: Vector3[] = [];

  for (let i = 0; i < CELLS; i++) {
    const angle = (i - (CELLS - 1) / 2) * angleStep;
    const centre = 1 - Math.abs(i - (CELLS - 1) / 2) / CELLS;
    const thickness = 0.28 + 0.3 * centre;
    const cell = new BoxGeometry(SPAN / CELLS * 1.02, thickness, CHORD * (0.86 + 0.14 * centre));
    // Place on the arc, centred LINE_LENGTH above the harness.
    const x = Math.sin(angle) * ARC;
    const y = LINE_LENGTH + Math.cos(angle) * ARC - ARC;
    cell.applyMatrix4(new Matrix4().makeRotationZ(-angle).setPosition(x, y + thickness / 2, 0));
    parts.push(painted(cell, STRIPES[i % 2 === 0 ? (i % 4 === 0 ? 0 : 2) : 1]));
    if (i % 2 === 0) {
      anchors.push(new Vector3(x, y, -CHORD * 0.35), new Vector3(x, y, CHORD * 0.3));
    }
  }

  // Suspension lines from the underside of alternate cells to the two risers.
  const up = new Vector3(0, 1, 0);
  for (const anchor of anchors) {
    const riser = new Vector3(anchor.x > 0 ? 0.22 : -0.22, 0.1, 0);
    const span = anchor.clone().sub(riser);
    const line = new CylinderGeometry(0.012, 0.012, span.length(), 3, 1, true);
    const rotation = new Quaternion().setFromUnitVectors(up, span.clone().normalize());
    line.applyMatrix4(new Matrix4().compose(riser.clone().add(span.multiplyScalar(0.5)), rotation, new Vector3(1, 1, 1)));
    parts.push(painted(line, 0x3a3a3a));
  }

  const geometry = mergeGeometries(parts);
  parts.forEach(p => p.dispose());
  const mesh = new Mesh(geometry, new MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    roughness: 0.7,
  }));
  mesh.castShadow = true;
  mesh.name = 'parachute';
  return mesh;
}
